import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

// ==================== CATEGORY API FUNCTIONS ====================

/**
 * Get list of all categories (Authenticated users)
 * @returns {Promise} Array of categories
 */
export const getAllCategories = async () => {
    try {
        const response = await api.get('/categories/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new category (Admin only)
 * @param {Object} categoryData - Category data object
 * @param {string} categoryData.name - Category name
 * @param {string} categoryData.description - Category description
 * @returns {Promise} Created category data
 */
export const createCategory = async (categoryData) => {
    try {
        const response = await api.post('/categories/', categoryData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing category (Admin only)
 * @param {number} categoryId - Category ID
 * @param {Object} categoryData - Updated category data
 * @returns {Promise} Updated category data
 */
export const updateCategory = async (categoryId, categoryData) => {
    try {
        const response = await api.patch(`/categories/${categoryId}/`, categoryData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a category (Admin only)
 * @param {number} categoryId - Category ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteCategory = async (categoryId) => {
    try {
        const response = await api.delete(`/categories/${categoryId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export default api;
